import React, { FC, useState } from 'react';
import CisDropdownPaper, {
  CisDropdownPaperProps,
} from '@components/EntityDrawers/components/ReferencesInputsGroup/components/CisDropdownPaper';
import CisReferenceChip from '@components/EntityDrawers/components/ReferencesInputsGroup/components/CisReferenceChip';
import IdTitle from '@components/EntityDrawers/components/ReferencesInputsGroup/components/IdTitle';
import IgBadge from '@components/EntityDrawers/components/ReferencesInputsGroup/components/IgBadge';
import useChangeReference from '@components/EntityDrawers/components/ReferencesInputsGroup/hooks/useChangeReference';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import useQnousRefOptions from '@components/EntityDrawers/hooks/useQnousRefOptions';
import ListItemWithTooltip from '@components/Inputs/Autocomplete/items/ListItemWithTooltip';
import ControlledAutocomplete from '@components/Inputs/controllers/ControlledAutocomplete';
import { ImplementationGroup, RefType } from '@constants/entities/project';
import { ISelectOption } from '@constants/entities/ui';
import { useProject } from '@context/Project';
import useCisTierMap from '@hooks/useCisTierMap';
import useProjectData from '@hooks/useProjectData';
import useReferenceMaps from '@hooks/useReferenceMaps';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { sortedTagEntries } from '@utils/implementationGroup';

const filterByQuery = createFilterOptions<ISelectOption>();

const CisInput: FC = () => {
  const { viewOnly } = useProject();
  const [{ cisOptions }, isRefsLoading] = useQnousRefOptions();
  const [{ cisMap }] = useReferenceMaps();
  const cisTierMap = useCisTierMap();
  const [{ settingsData }] = useProjectData();
  const [activeTiers, setActiveTiers] = useState<ImplementationGroup[]>([]);

  const { handleChangeReference, isChangingReference } = useChangeReference();

  if (!settingsData?.reference_model.includes(RefType.CIS)) return null;

  const resolveLabel = (option: ISelectOption) =>
    option.label || cisMap.get(String(option.value)) || '';

  return (
    <ControlledAutocomplete
      name={CommonKeys.CisRef}
      label="CIS-18 reference"
      placeholder="Select reference"
      options={cisOptions}
      loading={isRefsLoading || isChangingReference}
      multiple
      readOnly={viewOnly}
      onOpen={() => setActiveTiers([])}
      PaperComponent={CisDropdownPaper}
      slotProps={{
        paper: {
          activeTiers,
          onTiersChange: setActiveTiers,
        } as CisDropdownPaperProps,
      }}
      filterOptions={(options, state) => {
        const byQuery = filterByQuery(options, state);

        if (!activeTiers.length) return byQuery;

        return byQuery.filter((option) => {
          const tier = cisTierMap.get(String(option.value));

          return !!tier && activeTiers.includes(tier);
        });
      }}
      itemComponent={ListItemWithTooltip}
      itemProps={(option: ISelectOption) => {
        const tier = cisTierMap.get(String(option.value));

        return {
          endAdornment: tier ? <IgBadge tier={tier} /> : null,
          renderTooltipTitle: (tooltipOption: ISelectOption) => {
            const [id, title] = resolveLabel(tooltipOption).split(':');

            return <IdTitle id={id} title={title} color="grey.500" />;
          },
        };
      }}
      onChange={handleChangeReference(CommonKeys.CisRef)}
      isLoading={isChangingReference || isRefsLoading}
      renderTags={(value, getTagProps) =>
        sortedTagEntries(value).map(({ option, index }) => {
          const { key, ...chipProps } = getTagProps({ index });

          return (
            <CisReferenceChip
              key={key}
              label={resolveLabel(option)}
              tier={cisTierMap.get(String(option.value))}
              chipProps={chipProps}
            />
          );
        })
      }
    />
  );
};

export default CisInput;
